import {
  CenterItem,
  Column,
  Row,
  RowAlignedItems,
} from '@components/LinearLayout';
import AgendaFilter, {
  AgendaFilterRoot,
  FilterOptionText,
  FilterTitle,
  OptionsWrapper,
} from '@composites/AgendaFilter';
import {
  getAgendaFilters,
  getEventDetails,
  setAgendaFilters,
} from '@store/slices/eventSlice';
import { useDesktopWidth, useTabletWidth } from '@utils/hooks';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AgendaDates,
  ApplyButtonWrapper,
  ClearFilterWrapper,
  DateBox,
  DateBoxWrapper,
  DateText,
  DayItem,
  FilterBox,
  FilterDarkText,
  FilterModalRoot,
  FilterText,
  FiltersRoot,
  RotateIcon,
  UnselectedDates,
} from './styles';

import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Icon from '@components/Icon';
import RegularModal from '@components/RegularModal';
import { DayText } from '@composites/AgendaDates/styles';
import { AgendaDays } from '@pages/Agenda/helper';

import { extractSearchParams, generateFilterOptions } from '@utils/fn';
import { formatDate } from '@utils/time';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  AgendaSpeaker,
  PublicTrackItem,
} from '@hubs101/booking-api-skd-client/lib/types';

interface FiltersProps {
  sessions: any[];
  agendasTracks: PublicTrackItem[];
  sortedAgendaDaysFormat: string[];
  agendaDaysFormat: string[];
  activeDay: string;
  coLocatedEventsItems:
    | Array<{ id: string; key: string; value: string; color: string }>
    | undefined;
  selectedEvents: string[];
  setSelectedEvents: Dispatch<SetStateAction<string[]>>;
}

const Filters = ({
  sortedAgendaDaysFormat,
  sessions,
  agendasTracks,
  agendaDaysFormat,
  activeDay,
  coLocatedEventsItems,
  selectedEvents,
  setSelectedEvents,
}: FiltersProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useSelector(getAgendaFilters);
  const dispatch = useDispatch();
  const isDesktop = useDesktopWidth();
  const isTablet = useTabletWidth();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isDateHovered, setIsDateHovered] = useState<boolean>(false);
  const [isFilterHovered, setIsFilterHovered] = useState<boolean>(false);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [isOnTop, setIsOnTop] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, string[]>
  >({});

  const [selectedDate, setSelectedDate] = useState(activeDay);
  const [filteredSessions, setFilteredSessions] = useState<any[]>(
    sessions ?? []
  );
  const [isFilterDateHovered, setIsFilterDateHovered] = useState<
    number | null
  >();
  const [filteredCoEvents, setFilteredCoEvents] =
    useState<string[]>(selectedEvents);

  /* FILTER OPTIONS */
  const tracks = useMemo(
    () =>
      generateFilterOptions(
        filteredSessions
          ?.map(session => session?.tracks)
          .flat()
          .filter(t => t)
          ?.map(
            track => agendasTracks?.find(e => e.id === track)?.name as string
          ) ?? []
      ),
    [filteredSessions, agendasTracks]
  );
  const sessionTypes = useMemo(
    () => generateFilterOptions(filteredSessions?.map(e => e?.type) ?? []),
    [filteredSessions]
  );
  const sessionLocations = useMemo(
    () =>
      generateFilterOptions(
        filteredSessions?.map(e => e?.location.name)?.filter(l => l) ?? []
      ),
    [filteredSessions]
  );
  const speakerCompanyNames = useMemo(
    () =>
      generateFilterOptions(
        filteredSessions
          ?.map(e => e?.speakers.map((e: AgendaSpeaker) => e.company?.name))
          .flat()
          .filter(t => t) ?? []
      ),
    [filteredSessions]
  );

  const filterItems: Array<{
    id: string;
    title: string;
    values: Array<{
      key: string;
      value: string;
    }>;
  }> = useMemo(
    () => [
      {
        id: 'session_type',
        title: t('filters-session-type'),
        values: sessionTypes,
      },
      {
        id: 'tracks',
        title: t('filters-tracks'),
        values: tracks,
      },
      {
        id: 'location',
        title: t('filters-location'),
        values: sessionLocations,
      },

      {
        id: 'company_name',
        title: t('filters-company-name'),
        values: speakerCompanyNames,
      },
    ],
    [
      filteredSessions,
      sessionLocations,
      sessionTypes,
      speakerCompanyNames,
      t,
      tracks,
    ]
  );

  useEffect(() => {
    const coLocatedEventsIdSlugItems =
      coLocatedEventsItems?.map(e => {
        return { id: e.id, slug: e.key };
      }) ?? [];

    const coLovatedEventIds =
      coLocatedEventsIdSlugItems
        .filter(e => filteredCoEvents.includes(e?.slug ?? ''))
        ?.map(e => e.id) ?? [];

    const filteredNewSessions = sessions?.filter(session =>
      coLovatedEventIds.includes(session.event)
    );
    setFilteredSessions(filteredNewSessions);
  }, [sessions, filteredCoEvents, coLocatedEventsItems]);

  /* END FILTER OPTIONS */

  useEffect(() => {
    setFilteredCoEvents(selectedEvents);
  }, [selectedEvents]);

  useEffect(() => {
    const handleScroll = () => {
      if (componentRef.current != null) {
        const navEl = document.getElementById('global-nav');
        const navHeight = navEl ? navEl.getBoundingClientRect().height : 0;
        const rect = componentRef.current.getBoundingClientRect();
        const threshold = navHeight;
        setIsOnTop(rect.top <= threshold);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isDesktop, isTablet]);

  const isFiltersApplied = useMemo(
    () => Object.keys(filters)?.length > 0 || selectedEvents?.length > 1,
    [filters, selectedEvents?.length]
  );

  // Detect Safari browser
  const isSafari = useMemo(() => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  const resetFilters = useCallback(() => {
    dispatch(setAgendaFilters({}));
    setSelectedEvents([sluggedEvent?.slug]);
    setFilteredCoEvents([sluggedEvent?.slug]);
    setFilteredSessions(sessions);
  }, [
    dispatch,
    sluggedEvent?.slug,
    setSelectedEvents,
    setFilteredCoEvents,
    setFilteredSessions,
    sessions,
  ]);

  const setFilters = useCallback(() => {
    if (isFiltersApplied) {
      setAppliedFilters(filters);
    } else setAppliedFilters({});
  }, [filters, isFiltersApplied]);

  useEffect(() => {
    setFilters();
  }, [setFilters]);

  const onApply = useCallback(() => {
    setShowModal(false);
    setIsFilterHovered(false);
    if (Object.keys(appliedFilters)?.length > 0) {
      dispatch(setAgendaFilters(appliedFilters));
    } else dispatch(setAgendaFilters(appliedFilters));
    setSelectedEvents(filteredCoEvents);
    setSearchParams({
      ...extractSearchParams(searchParams),
      day: selectedDate,
    });
  }, [
    appliedFilters,
    dispatch,
    filteredCoEvents,
    searchParams,
    selectedDate,
    setSearchParams,
    setSelectedEvents,
  ]);

  const filtersModal = useMemo(() => {
    return (
      <FilterModalRoot>
        <AgendaFilterRoot $gap="2rem">
          {isTablet && (
            <AgendaDates>
              {agendaDaysFormat?.map((e, index) => (
                <DayItem
                  key={e}
                  $active={e === selectedDate}
                  onMouseOver={() => {
                    setIsFilterDateHovered(index);
                  }}
                  onMouseOut={() => {
                    setIsFilterDateHovered(null);
                  }}
                  onClick={() => {
                    setSelectedDate(e);
                  }}
                >
                  <DayText
                    $active={e === selectedDate}
                    $isHovered={isFilterDateHovered === index}
                  >
                    {agendaDaysFormat?.length === 1
                      ? `${dayjs(formatDate(e)).format('DD.MM.YYYY')}`
                      : `${t(AgendaDays[index])} ${dayjs(formatDate(e)).format('DD.MM.YYYY')}`}
                  </DayText>
                </DayItem>
              ))}
            </AgendaDates>
          )}
          <FilterTitle>{t('event')}</FilterTitle>
          <OptionsWrapper $gap="3rem">
            {coLocatedEventsItems?.map(({ key, value, color }) => (
              <RowAlignedItems key={key}>
                <Checkbox
                  label={
                    <RowAlignedItems>
                      <FilterOptionText>{value}</FilterOptionText>
                    </RowAlignedItems>
                  }
                  spacing={theme.spacing.size10}
                  checked={Boolean(filteredCoEvents?.includes(key))}
                  type="square"
                  size="3.2rem"
                  backgroundColor={
                    color?.length ? color : theme.color.lightGrey
                  }
                  color={
                    color?.length ? theme.color.white : theme.color.mobilityGrey
                  }
                  borderRadius={theme.borderRadius.radius5}
                  border="none"
                  onChange={() => {
                    if (
                      filteredCoEvents?.length < 2 &&
                      filteredCoEvents?.includes(key)
                    )
                      setFilteredCoEvents([key]);
                    else if (filteredCoEvents?.includes(key))
                      setFilteredCoEvents(
                        filteredCoEvents?.filter(e => e !== key)
                      );
                    else setFilteredCoEvents([...filteredCoEvents, key]);
                  }}
                />
              </RowAlignedItems>
            ))}
          </OptionsWrapper>
        </AgendaFilterRoot>
        <Column $gap="3rem">
          {filterItems?.map(item => (
            <div key={item.id}>
              {item?.values?.length > 0 && (
                <AgendaFilter
                  item={item}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                />
              )}
            </div>
          ))}
        </Column>
      </FilterModalRoot>
    );
  }, [
    isTablet,
    agendaDaysFormat,
    t,
    coLocatedEventsItems,
    filterItems,
    selectedDate,
    isFilterDateHovered,
    theme.spacing.size10,
    theme.color.lightGrey,
    theme.color.white,
    theme.color.mobilityGrey,
    theme.borderRadius.radius5,
    filteredCoEvents,
    appliedFilters,
  ]);

  const filteredAgendaDays = useMemo(() => {
    if (isDateHovered) return sortedAgendaDaysFormat;
    return [sortedAgendaDaysFormat?.[sortedAgendaDaysFormat?.length - 1]];
  }, [isDateHovered, sortedAgendaDaysFormat]);

  const selectedAgendaDay = useMemo(
    () => sortedAgendaDaysFormat?.[sortedAgendaDaysFormat?.length - 1],
    [sortedAgendaDaysFormat]
  );
  const unselectedAgendaDays = useMemo(
    () => sortedAgendaDaysFormat?.slice(0, sortedAgendaDaysFormat?.length - 1),
    [sortedAgendaDaysFormat]
  );

  return (
    <FiltersRoot
      ref={componentRef}
      $isOnTop={isOnTop}
      $navHeightPx={
        document.getElementById('global-nav')?.getBoundingClientRect().height ??
        85
      }
    >
      {isOnTop && !isTablet && (
        <DateBoxWrapper
          onMouseOver={() => {
            setIsDateHovered(true);
          }}
          onMouseOut={() => {
            setIsDateHovered(false);
          }}
        >
          <UnselectedDates $isDateHovered={isDateHovered}>
            {unselectedAgendaDays?.map((e, index) => (
              <DateBox
                key={e}
                $isDateHovered={isDateHovered}
                $isOnTop={isOnTop}
                $isFirst={index === 0}
                $isLast={false}
                onClick={() => {
                  setSearchParams({
                    ...extractSearchParams(searchParams),
                    day: agendaDaysFormat?.[AgendaDays?.indexOf(e)],
                  });
                }}
              >
                <Row $gap="0.2rem">
                  <DateText $color={sluggedEvent?.design.themeColor}>
                    {t(unselectedAgendaDays[index])}
                  </DateText>
                </Row>
              </DateBox>
            ))}
          </UnselectedDates>
          <DateBox
            $isDateHovered={isDateHovered}
            $isOnTop={isOnTop}
            $isFirst={filteredAgendaDays?.length === 1}
            $isLast
          >
            <Row $gap="0.2rem">
              <DateText $color={sluggedEvent?.design.themeColor}>
                {t(selectedAgendaDay)}
              </DateText>
              <CenterItem>
                <RotateIcon $isDateHovered={isDateHovered}>
                  <Icon
                    size={18}
                    color={theme.color.white}
                    name="caret_arrow"
                    indentLess
                  />
                </RotateIcon>
              </CenterItem>
            </Row>
          </DateBox>
        </DateBoxWrapper>
      )}
      <FilterBox
        $isOnTop={isOnTop}
        $isFiltersApplied={isFiltersApplied}
        $isTablet={isTablet}
        $isSafari={isSafari}
      >
        <Row $gap="2rem">
          <Row
            onMouseOver={() => {
              setIsHovered(true);
            }}
            onMouseOut={() => {
              setIsHovered(false);
            }}
            $gap="0.5rem"
            onClick={() => {
              setShowModal(true);
            }}
          >
            <FilterText
              $isHovered={isHovered}
              $color={sluggedEvent?.design?.themeColor}
            >
              {t('filters')}
            </FilterText>
            <CenterItem>
              <Icon
                size={24}
                name="settings"
                color={
                  isHovered
                    ? sluggedEvent?.design?.themeColor
                    : theme.color.white
                }
                indentLess
                onClick={() => {
                  setShowModal(true);
                }}
              />
            </CenterItem>
          </Row>
          {isFiltersApplied && (
            <ClearFilterWrapper
              $gap="0.5rem"
              onClick={resetFilters}
              onMouseOver={() => {
                setIsFilterHovered(true);
              }}
              onMouseOut={() => {
                setIsFilterHovered(false);
              }}
            >
              <FilterDarkText
                $color={
                  isFilterHovered
                    ? sluggedEvent?.design?.themeColor
                    : theme.color.white
                }
              >
                {t('reset-filters')}
              </FilterDarkText>
              <CenterItem>
                <Icon
                  size={24}
                  name="close"
                  color={
                    isFilterHovered
                      ? sluggedEvent?.design?.themeColor
                      : theme.color.white
                  }
                  indentLess
                />
              </CenterItem>
            </ClearFilterWrapper>
          )}
        </Row>
      </FilterBox>
      <RegularModal
        visible={showModal}
        maxHeight={isTablet ? `calc(100vh - 6rem)` : `calc(100vh - 18rem)`}
        overflow="auto"
        width={isTablet ? '100vw' : '90vw'}
        onClose={() => {
          setShowModal(false);
          setFilters();
        }}
        content={filtersModal}
        title={
          <Row $gap="3rem">
            <Row $gap="0.5rem">
              <FilterText $isHovered $color={theme.color.mobilityGrey}>
                {t('filters')}
              </FilterText>
              <CenterItem>
                <Icon
                  size={24}
                  name="settings"
                  color={theme.color.mobilityGrey}
                  indentLess
                />
              </CenterItem>
            </Row>
            {isFiltersApplied && (
              <ClearFilterWrapper
                $gap="0.5rem"
                onClick={resetFilters}
                onMouseOver={() => {
                  setIsFilterHovered(true);
                }}
                onMouseOut={() => {
                  setIsFilterHovered(false);
                }}
              >
                <FilterText $isHovered $color={theme.color.mobilityGrey}>
                  {t('reset-filters')}
                </FilterText>
                <CenterItem>
                  <Icon
                    size={24}
                    name="close"
                    color={theme.color.mobilityGrey}
                    indentLess
                  />
                </CenterItem>
              </ClearFilterWrapper>
            )}
          </Row>
        }
        ModalFooter={
          <ApplyButtonWrapper>
            <Button
              title={t('apply')}
              backgroundColor={sluggedEvent?.design.themeColor}
              hoverBackgroundColor={theme.color.mobilityGrey}
              textColor={theme.color.white}
              borderRadius={theme.borderRadius.radius10}
              padding="0.6rem 2.5rem"
              width="max-content"
              onClick={onApply}
            />
          </ApplyButtonWrapper>
        }
      />
    </FiltersRoot>
  );
};

export default Filters;
